import "../../../Cryptography/Crypto/SigningSession";

import { AppContext, bootstrapApp } from "App";

import { ISectionLoggerFactory, LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { bridge } from "@multiweb/reactCore/bridge";
import { ReduxRegistrator } from "@multiweb/reactCustomization";
import { configureReact, StoreManager } from "@multiweb/reactSetup";
import { ILanguage } from "@multiweb/types/contracts";
import { AppStorageItemType, CancellationToken } from "@multiweb/types/multiweb";
import { FormatProviderRegistrator } from "Global/Base/FormatProviderRegistrator/FormatProviderRegistrator";
import { NUMERAL_DATA_FORMAT, numeralDataFormat } from "Global/Base/NumeralDataFormat/NumeralDataFormat";
import { RightsEvaluatorFactory } from "Global/Base/Rights/RightsEvaluator/RightsEvaluatorFactory";
import { IRightsEvaluatorFactory } from "Global/Base/Rights/RightsEvaluator/types";
import { ServiceWorkerMessageType } from "Global/Base/ServiceWorkerMessages";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { registerDefaultLogging } from "Logging/defaultRegistrator";
import { EOWSessionTimeOut, OWExceptions } from "OW.Remoting/Basic/OWExceptions";
import { ServiceWorkerConnector } from "OW.Remoting/Basic/ServiceWorkerConnector";
import { IAuthManager } from "OW.Remoting/Remoting/Auth/IAuthManager";
import { StandardAuthManager } from "OW.Remoting/Remoting/Auth/StandardAuthManager";
import { ClientType, IClientFactory } from "OW.Remoting/Remoting/Clients/api";
import { DataEventsManager } from "OW.Remoting/Remoting/DataEventsManager";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { IUserSettingsManager } from "OW.Remoting/Remoting/IUserSettingsManager";
import { IWorkflowManager } from "OW.Remoting/Remoting/IWorkflowManager";
import { UserSettingsManager } from "OW.Remoting/Remoting/UserSettingsManager";
import { ErrorHandler, IErrorHandler, StandardErrorHandler } from "UI/Errors";
import { ApplicationTheme } from "UI/Shared/ApplicationTheme";
import { createReloadMenuAction } from "UI/UI.React/menu";
import { ISigningAppCommunicator, ISigningStreamCreator } from "../../../Cryptography/Crypto/CryptoInterfaces";
import { SigningExtensionCommunicator } from "../../../Cryptography/Crypto/SigningExtensionCommunicator";
import { DIContainer } from "../../../Global/Base/DIContainer";
import { Event, StatefulEvent } from "../../../Global/Base/Events";
import { GlobalContext } from "../../../Global/Base/GlobalContext";
import { IRightsEvaluationScheduler } from "../../../Global/Base/Rights/IRightsEvaluationScheduler";
import { IRightsManager } from "../../../Global/Base/Rights/IRightsManager";
import { ParallelRightsEvaluationScheduler } from "../../../Global/Base/Rights/ParallelRightsEvaluationScheduler";
import { DateUtils } from "../../../Global/Base/Utils/DateUtils";
import { EnumUtils } from "../../../Global/Base/Utils/EnumUtils";
import { FileUtils } from "../../../Global/Base/Utils/FileUtils";
import { ApplicationStorage } from "../../../OW.Remoting/Remoting/ApplicationStorage";
import { AppMemCache } from "../../../OW.Remoting/Remoting/AppMemCache";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { IModelDescriptionsManager } from "../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { StandardModelDescriptionsManager } from "../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IAppCache } from "../../../OW.Remoting/Remoting/Cache/IAppCache";
import { getDataReaderClient } from "../../../OW.Remoting/Remoting/Clients/impl/utils/clientGetters";
import { Flags } from "../../../OW.Remoting/Remoting/DataTypes/Flags";
import { ExportManagerWorker, IExportManagerWorker } from "../../../OW.Remoting/Remoting/ExportManager";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { INotificationManager, NotificationManager } from "../../../OW.Remoting/Remoting/Notifications";
import { FormOpenMode, FormOpenType } from "../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { WorkflowManager } from "../../../OW.Remoting/Remoting/WorkflowManager";
import { ValidityStateHelper } from "../../UI.ElementarControls/Helpers/ValidityStateHelper";
import menuModuleRegistrator from "../../UI.React/menu/customization/registrator";
import { MenuAppConnector } from "../../UI.React/MenuAppConnector";
import { ComponentTree } from "../LogicalTree/ComponentTree";
import { DynamicComponentTree } from "../LogicalTree/DynamicComponentTree";
import { MSLOVComponentTree } from "../LogicalTree/MSLOVComponentTree";
import { RightsEvaluationOverridesType } from "../Rights/Definitions";
import { RightsManager } from "../Rights/RightsManager";
import { BreadcrumbNav } from "../UI/BreadcrumbNav";
import { IFRMParams } from "../UI/Controls/FormParameters";
import { SigningStreamCreator } from "../UI/Controls/SigningStreamCreator";
import { DefUploadManager } from "../UI/DefUploadManager";
import { DownloadManager } from "../UI/DownloadManager";
import { ChartFrameFormBuilder } from "../UI/FormBuilders/ChartFrameFormBuilder";
import { IChartFrameFormBuilder, IShowArchiveVersionsFormBuilder } from "../UI/FormBuilders/IFormBuilder";
import { ISettingWindowFormBuilder } from "../UI/FormBuilders/ISettingWindowFormBuilder";
import { SettingWindowFormBuilder } from "../UI/FormBuilders/SettingWindowFormBuilder";
import { ShowArchiveVersionsFormBuilder } from "../UI/FormBuilders/ShowArchiveVersionsFormBuilder";
import { DefFormManager } from "../UI/FormManager/DefFormManager";
import { FormManager, IFormManager } from "../UI/FormManager/IFormManager";
import { IFormGroupMenu, IFormUI } from "../UI/FormUI/FormUI";
import { FormGroupMenuStandard, FormUIStandard } from "../UI/FormUI/FormUIStandard";
import { IOpenedFormsHistoryManager } from "../UI/Helpers/IOpenedFormsHistoryManager";
import { OpenedFormsHistoryManager } from "../UI/Helpers/OpenedFormsHistoryManager";
import { ISystemOperationRights, SystemOperationRights } from "../UI/Helpers/SystemOperationRights";
import { IBreadcrumbNav } from "../UI/IBreadcrumbNav";
import { RemotingValidationLocalizer } from "../UI/RemotingValidationLocalizer";
import { IUIManagerWorker, UIManager } from "../UI/UIManager";
import { IMainMenu } from "../UI/UIManagerInterfaces";
import { IUploadManager } from "../UI/UploadManager";
import { OpenModeType } from "../UI/XGControls/Actions/DetailFormAction";
import { ExtendedFilterParser } from "../UI/XGControls/Filters/AdvancedFilters/ExtendedFilterParser";
import { IExtendedFilterParser } from "../UI/XGControls/Filters/FilterInterfaces";
import { FrameSharedMethods } from "../UI/XGControls/Frames/FrameSharedMethods";
import { IForm } from "../UI/XGControls/IForm";
import { IFrameWithRights } from "../UI/XGControls/IFrame";
import { convertFormOpenTypeToPurposeType, IPageParams, parsePageUrl } from "../Utilities/SharedUIMethods";
import { AppUpdateProgress } from "./AppUpdateProgress";
import { ComponentDefinitions } from "./ComponentDefinitions";
import { DefUIManagerWorker } from "./DefUIManagerWorker";
import { IKeyboardModifiersWatcher } from "./IKeyboardModifiersWatcher";
import { IUserActionLogger } from "./IUserActionLogger";
import { KeyboardModifiersWatcher } from "./KeyboardModifiersWatcher";
import { UserActionLogger } from "./UserActionLogger";

const { dispatchAction } = bridge;

/** ID elementu, do kterého se zapojí hlavní menu aplikace. */
const MENU_ROOT_ELEMENT_ID = "formsMenu";

/**
 * @includeToDoc
 */
export class MainForm {
    //** Parametry stránky */
    private static _pageConfig: IPageParams;

    private static _userSettingsManager: IUserSettingsManager;

    /**
     * Událost určená pro registrování vlastních implementací do Dependency Injection kontejneru
     */
    public static readonly registrations: StatefulEvent<MainForm, void> = new StatefulEvent<MainForm, void>();
    /**
     * Událost volaná po kompletní inicializaci MainFormu
     */
    public static readonly initialized: StatefulEvent<MainForm, void> = new StatefulEvent();
    /**
     * Událost vyvolaná po změne aktivních rolí
     */
    public static readonly activeRolesChanged: Event<MainForm, void> = new Event<MainForm, void>();

    public static async init(): Promise<void> {
        await bootstrapApp({
            serviceWorkerOptions: {
                onUpdateFound: () => AppUpdateProgress.showUpdateInProgress("#blockingLayer", createDefaultSectionLoggerFactory(AppContext.instance.configuration.logging).create(LoggingSection.app)),
                onUpdateReady: () => AppUpdateProgress.showUpdateReady("#blockingLayer", createDefaultSectionLoggerFactory(AppContext.instance.configuration.logging).create(LoggingSection.app)),
                heartBeat: true,
            },
        });

        const defaultLogger = createDefaultSectionLoggerFactory(AppContext.instance.configuration.logging).create(LoggingSection.app);

        MainForm._pageConfig = parsePageUrl(AppContext.instance.addressParams, defaultLogger);

        MainForm._registerModules();

        const clientFactory = DIContainer.instance.create(IClientFactory);

        MainForm._registerDefaultTypes(clientFactory, MainForm._pageConfig);

        ErrorHandler.instance.activateAsyncGlobalErrorHandler();

        await MainForm._loadUserData(clientFactory);

        await MainForm._initActiveRoles();

        configureReact();

        MainForm._initReduxStore();

        await MainForm._initCulture(clientFactory);

        // inicializace UIManageru potřebuje mít zinicializovaný jazyk/kulturu
        UIManager.instance.init();

        MainForm._preloadLocalizations();

        DIContainer.instance.create(IUserActionLogger)?.registerWindow(window);

        await MainForm._loadFlagTypes();

        MainForm._initReports();

        MainForm._setTheme();

        MainForm._setUsedFormUi();

        await MainForm._openFormIfNeeded(MainForm._pageConfig);

        MainForm._registerActiveRolesSubscriber();

        MainForm._watchDataChanges();

        MainForm._watchSessionTimeout();

        MainForm._setInitialized();
    }

    private static _preloadLocalizations(): void {
        // validační lokalizace se budou načítat ze serveru a načítání se spustí hned
        ValidityStateHelper.validityLocalizer = new RemotingValidationLocalizer();
    }

    /** Zaregistruje přepisovatelné moduly aplikace */
    private static _registerModules(): void {
        menuModuleRegistrator.registerReplaceableComponents();
    }

    private static _setUsedFormUi(): void {
        document.querySelector("body").classList.add(`form-ui-${AppContext.instance.configuration.formUI.toLowerCase()}`);
    }

    private static _setInitialized(): void {
        // detekce dokončení inicializace pro testy
        document.documentElement.setAttribute("data-initialized", "");
        MainForm.initialized.trigger(this);
    }

    private static async _openFormIfNeeded(pageConfig: IPageParams): Promise<void> {
        const openFormName: string | null = pageConfig.formCode;
        const pageFormParams = await MainForm._processAddressParams(pageConfig);
        //const appMainElement = document.getElementById("appMain");
        //const hideMainMenu = (appMainElement != null ? appMainElement.classList.contains("no-menu") : false) || pageConfig.hideMenu;

        if (openFormName) {
            pageFormParams.isFromMainMenu = true;
            await FormManager.instance.openForm(openFormName, pageFormParams);
        }
    }

    private static async _loadFlagTypes(): Promise<void> {
        await GlobalContext.getType<typeof Flags>(Flags).loadFlags();
    }

    private static _initReports(): void {
        if (!ApplicationStorage.get(AppStorageItemType.reportType))
            ApplicationStorage.set(AppStorageItemType.reportType, AppContext.instance.configuration.reportType);
    }

    /** Nastavení titulku okna. Pokud jsou nastavené i menuItemId nebo formCode, tak se přidají patřičné názvy do titulku. */
    private static _setWindowTitleWithFormName(clientFactory: IClientFactory): void {
        const menuItemId = MainForm._pageConfig.menuItemId;
        const formCode = MainForm._pageConfig.formCode;
        const appName = AppContext.instance.configuration.appName;
        MainForm._setTitle(appName);
        if (menuItemId) {
            // Pokud máme menuItemId, načteme si lokalizační klíč z položky menu.
            let condition = `id = ${menuItemId}`;
            MainForm._fetchAndSetTitle(clientFactory, "CPolozka_Menu", condition, "lokal_klic").catch(() => {
                // V případě jakéhokoliv selhání fallback na formCode.
                condition = `DB.Upper(kod_formulare) = DB.Upper('${formCode}')`;
                MainForm._fetchAndSetTitle(clientFactory, "CFormular", condition, "lokalizacni_klic");
            });
        } else {
            // Pokud nemáme menuItemId, fallback na formCode. Musíme si dotazem na server načíst locKey formuláře.
            const condition = `DB.Upper(kod_formulare) = DB.Upper('${formCode}')`;
            MainForm._fetchAndSetTitle(clientFactory, "CFormular", condition, "lokalizacni_klic");
        }
    }

    /** Načte příšlušný lokalizační klíč ze služby podle předané podmínky a atributu a nastaví lokalizovaný titulek stránky. */
    private static _fetchAndSetTitle(clientFactory: IClientFactory, className: string, condition: string, attribute: string): Promise<void> {
        const appName = AppContext.instance.configuration.appName;
        return clientFactory.get(ClientType.dataReader).getDataRowsWithParams(className, [attribute], `ID ≠ 0 ∧ ${attribute} ≠ ∅ ∧ ${condition}`, { count: 1 })
            .then(async (rows) => {
                if (rows && rows.length > 0) {
                    const locKey = AttrValueReader.read(rows[0][attribute]);
                    if (locKey) {
                        const formTitle = await Localization.getTextPromise(MainForm._correctLockKey(locKey));
                        MainForm._setTitle(`${appName} - ${formTitle}`);
                    }
                }
            });
    }

    private static _watchDataChanges(): void {
        const dataEventsManager = DIContainer.instance.create(IDataEventsManager);
        const workflowManager = DIContainer.instance.create(IWorkflowManager);
        dataEventsManager.watchObjectsChanges();
        workflowManager.subscribeWFOpenForm();
    }

    /** Nastavení uživatelem preferovaného barevného schématu */
    private static _setTheme(): void {
        let storedTheme = EnumUtils.getEnumValueFromName(ApplicationTheme, ApplicationStorage.get(AppStorageItemType.userTheme));
        if (storedTheme == null)
            storedTheme = ApplicationTheme.light;
        UIManager.instance.applyApplicationTheme(storedTheme);
    }

    /** Načtení metadat o aktuálním uživateli */
    private static async _loadUserData(clientFactory: IClientFactory): Promise<void> {
        const userClient = clientFactory.get(ClientType.user);
        try {
            AppContext.instance.setCurrentUser(await userClient.getCurrentUserInfo());
        } catch (e) {
            ErrorHandler.instance.handleError(e);
        }
    }

    /** Načte a aplikuje poslední použitou kulturu */
    private static async _initCulture(clientFactory: IClientFactory): Promise<void> {
        Localization.languageChanged.add((_sender, { language }) => {
            ServiceWorkerConnector.sendMessage({ type: ServiceWorkerMessageType.languageChanged, payload: { language } });
            ApplicationStorage.set(AppStorageItemType.language, language);
            MainForm._setWindowTitleWithFormName(clientFactory);
        });

        const cultureClient = clientFactory.get(ClientType.culture);

        const languages = await cultureClient.getSupportedLanguages();
        const langCode = await cultureClient.loadLanguageProfile(CancellationToken.none);
        let langData: ILanguage;
        if (langCode) {
            langData = languages.find(a => a.code === langCode);
        } else {
            langData = languages.find(a => a.isPrimary);
            await cultureClient.saveLanguageProfile(langData.code, CancellationToken.none);
        }
        if (!langData) {
            langData = await cultureClient.getCurrentLanguage();
        }

        Localization.setLanguage(langData.code, langData.culture);
        ApplicationStorage.set(AppStorageItemType.reportLanguage, langData.code);
    }

    private static _correctLockKey(locKey: string | number): string {
        locKey = locKey + ""; //chodi sem i cisla
        if (!/-/.test(locKey)) { //podminka, ze lok. klic nema zadny prefix (pak se pouzije defaultni XG)
            locKey = `XG-${locKey}`;
        }
        return locKey;
    }

    /** Nastaví titulek okna */
    private static _setTitle(title: string): void {
        document.title = title;
        let metaTitle = document.querySelector("meta[name=application-name]") as HTMLMetaElement;
        if (metaTitle)
            metaTitle.content = title;
        metaTitle = document.querySelector("meta[name=apple-mobile-web-app-title]") as HTMLMetaElement;
        if (metaTitle)
            metaTitle.content = title;
    }

    public static _registerDefaultTypes(clientFactory: IClientFactory, pageConfig: IPageParams): void {
        // registrování standardních typů sloupců pro seznamy
        FrameSharedMethods.registerStandardListColumnTypes();

        const context = AppContext.instance;
        const configuration = context.configuration;
        const di = DIContainer.instance;

        const useLegacyWebServiceApi = isVersionOlderOrEqual(configuration.getDevelopmentOption("webServiceVersion"), "15.03");

        // registrování továrních metod pro vytváření standardních komponent
        ComponentDefinitions.registerStandardDefinitions(di);
        di.register(IUserActionLogger, UserActionLogger, true);
        di.register(IKeyboardModifiersWatcher, KeyboardModifiersWatcher, true);
        di.registerFactory(ISigningAppCommunicator, () => new SigningExtensionCommunicator(() => di.create(IUserActionLogger)?.logEvent));
        di.registerFactory(IFormUI, (form: IForm) => new FormUIStandard(form, () => clientFactory.get(ClientType.culture).getSupportedLanguages(undefined)));
        di.registerFactory(INotificationManager, () => new NotificationManager(clientFactory.get(ClientType.notifications)), true);
        di.registerFactory(IFormGroupMenu, () => new FormGroupMenuStandard(), true);
        di.registerFactory(IOpenedFormsHistoryManager,
            () => new OpenedFormsHistoryManager(
                clientFactory.get(ClientType.userSettings),
                configuration.openedFormsHistoryCount,
            ), true);
        di.registerFactory(IMainMenu,
            () => new MenuAppConnector(
                MENU_ROOT_ELEMENT_ID,
                !!configuration.developmentMode,
                configuration.menuItems,
                !!configuration.showMainMenuExpanded,
                !!configuration.showMainMenuCaptions,
            ), true);
        di.registerFactory(IUIManagerWorker,
            () => new DefUIManagerWorker(
                document.getElementById("formsPlace"),
                document.getElementById("loader"),
                configuration.preloadFormCount,
                pageConfig.hideMenu ? null : di.createFactory(IMainMenu),
                di.create(IDataEventsManager),
            ), true);
        di.registerFactory(IErrorHandler,
            () => new StandardErrorHandler(
                !!configuration.developmentMode,
                di.create(IUIManagerWorker),
                di.create(ISectionLoggerFactory).create(LoggingSection.coreUI),
            ), true);
        di.registerFactory(IExportManagerWorker, () => new ExportManagerWorker(context.addresses.serviceUrl, useLegacyWebServiceApi), true);
        di.registerFactory(IModelDescriptionsManager, () => new StandardModelDescriptionsManager(di.create(ISectionLoggerFactory).create(LoggingSection.modelDescription)), true);
        di.registerFactory(IAppCache, () => new AppMemCache(), true);
        di.registerFactory(ISigningStreamCreator, () => new SigningStreamCreator(), true);
        di.registerFactory(IUploadManager, () => new DefUploadManager(), true);
        di.registerFactory(IBreadcrumbNav, () => new BreadcrumbNav());
        di.registerFactory(IShowArchiveVersionsFormBuilder, () => new ShowArchiveVersionsFormBuilder(), true);
        di.registerFactory(ISystemOperationRights, () => new SystemOperationRights(
            di.create(ICommunicationManager),
            di.create(ISectionLoggerFactory).create(LoggingSection.rights),
        ), true);
        di.registerFactory(IChartFrameFormBuilder, () => new ChartFrameFormBuilder(), true);
        di.registerFactory(IExtendedFilterParser, () => new ExtendedFilterParser());
        di.registerFactory(IRightsEvaluatorFactory, () => new RightsEvaluatorFactory(di.create(ISectionLoggerFactory).create(LoggingSection.rights)));
        di.registerFactory(IFormManager,
            () => new DefFormManager(
                document.getElementById("formsPlace"),
                di.create(IOpenedFormsHistoryManager),
                MainForm._pageConfig.previewId,
            ),
            true,
        );

        di.registerFactory(IAuthManager,
            () => new StandardAuthManager(
                clientFactory.get(ClientType.authentication),
                clientFactory.get(ClientType.user),
                configuration.clientCode,
                di.create(ISectionLoggerFactory).create(LoggingSection.authentication),
            ), true);

        //di.registerFactory(IRightsEvaluationScheduler, (id) => new CancelRunningRightsEvaluationScheduler(id, new ConsoleLogger()));
        di.registerFactory(IRightsEvaluationScheduler, (_id) => new ParallelRightsEvaluationScheduler());
        di.registerFactory(IRightsManager, (frame: IFrameWithRights, rightsEvaluationOverrides: RightsEvaluationOverridesType) =>
            new RightsManager(frame, rightsEvaluationOverrides, di.create(IRightsEvaluatorFactory), di.createFactory(IRightsEvaluationScheduler),
                di.create(ISectionLoggerFactory).create(LoggingSection.rights)),
        );
        di.registerFactory(IDataEventsManager, () => new DataEventsManager(
            clientFactory.get(ClientType.dataEvents), di.create(ISectionLoggerFactory).create(LoggingSection.dataEvents),
        ), true);
        di.registerFactory(IWorkflowManager, () => new WorkflowManager(di.create(IDataEventsManager)), true);
        di.registerFactory(ISettingWindowFormBuilder, () => new SettingWindowFormBuilder());

        di.registerFactory(IUserSettingsManager, () => new UserSettingsManager(clientFactory.get(ClientType.userSettings)));

        DownloadManager.archiveNameGeneratorFunction = async () => {
            return `${FileUtils.replaceInvalidFileNameChars(configuration.appName)}-${DateUtils.dateToString(new Date(), "yyyy-MM-dd--HH-mm-ss")}`;
        };

        ComponentTree.registerSpecialNodeFactory(MSLOVComponentTree, MSLOVComponentTree.createNodeFactory);
        ComponentTree.registerSpecialNodeFactory(DynamicComponentTree, DynamicComponentTree.createNodeFactory);

        registerDefaultLogging(AppContext.instance.configuration.logging);
        ReduxRegistrator.init(di.create(ISectionLoggerFactory).create(LoggingSection.coreRedux));

        //zaregistrování formátovací funkce na základě numeral.js použité jako výchozí pro ReportingScalar
        FormatProviderRegistrator.register(NUMERAL_DATA_FORMAT, numeralDataFormat);

        try {
            // touto událostí se mohou registrovat vlastní implementace, takže se před ní nesmí vytvářet instance (nebo iniciovat) třídy, které se vytvářejí přes DI
            MainForm.registrations.trigger(MainForm, undefined);
        } catch (e) {
            const logger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.app);
            logger.error("Error registering custom scripts!", e);
        }

        // Logger - nastavení správných závislostí. Až od teď je možné logovat přes Logging modul.
        LazyLoadingSectionLogger.initDependencies(DIContainer.instance.create(ISectionLoggerFactory));

        if (configuration.isExperimentEnabled("EasterEggs")) {
            try {
                //@ts-ignore
                import("../UI/Nyan");
            } catch (e) {
                // ignore
            }
        }
    }

    /** Přednaplní objekt s parametry pro otevření formuláře na základě url parametrů stránky */
    private static async _processAddressParams(pageParams: IPageParams): Promise<IFRMParams> {
        const formParams: IFRMParams = { condition: "", selectedID: null };

        if (pageParams.formOpenType != undefined)
            formParams.purposeType = convertFormOpenTypeToPurposeType(pageParams.formOpenType);

        // Pokud se otevírá parametrický formulář, tak se tyto parametry ignorují
        if (formParams.purposeType == null) {
            if (pageParams.condition != undefined)
                formParams.condition = pageParams.condition;

            if (pageParams.selectFirstRecord != undefined) {
                formParams.autoSelectFirstRow = pageParams.selectFirstRecord;
                if (formParams.autoSelectFirstRow)
                    formParams.autoSelectOnlyIfOneRow = false;
            }

            if (pageParams.selectedId != undefined)
                formParams.selectedID = pageParams.selectedId;

            if (pageParams.visibleOnlyDetail != null) {
                formParams.visibleOnlyDetail = pageParams.visibleOnlyDetail;
            }
        }

        if (pageParams.roles != null || pageParams.rolesIDs != null) {
            let rolesIDs = new Array<string>();

            if (pageParams.roles != null) {
                const roles = pageParams.roles.toLowerCase().split(",");
                const userRoles: { code: string; sid: string; id: string }[] = Object.values(await this._userSettingsManager.getRoles()).map(x => ({ code: x.code.toLowerCase(), sid: x.id.toString(), id: x.id }));
                for (const r of roles) {
                    const userRole = userRoles.find(x => x.code === r || x.sid === r);
                    if (userRole)
                        rolesIDs.push(userRole.id);
                }
            }

            if (pageParams.rolesIDs != null) {
                rolesIDs = rolesIDs.concat(pageParams.rolesIDs.split(","));
            }

            formParams.formContext = {
                roles: rolesIDs.length > 0 ? rolesIDs : ["-1"],
            };
        }

        if (pageParams.formProfileId)
            formParams.applyFormProfileId = pageParams.formProfileId;

        if (pageParams.menuItemId)
            formParams.menuItemId = pageParams.menuItemId;

        if (pageParams.formOpenMode >= 0 || pageParams.formOpenType >= 0) {
            if (pageParams.formOpenMode === FormOpenMode.edit) {
                formParams.openInEdit = true;
            }
            const allowedFormOpenTypes = [FormOpenType.paramConditionForm, FormOpenType.paramForm,
                FormOpenType.paramFormMethod, FormOpenType.paramFormReport];
            if (pageParams.formOpenMode === FormOpenMode.insert || allowedFormOpenTypes.includes(pageParams.formOpenType)) {
                if (pageParams.masterClassName && pageParams.masterCondition) {
                    const linkObject = await getDataReaderClient().getDataRowsWithParams(pageParams.masterClassName,
                        ["ID"], "ID > 0 AND " + pageParams.masterCondition, { count: 1 });
                    formParams.setAttributesOnNewObject = [{ attrName: pageParams.masterLinkToAttr, value: AttrValueReader.read(linkObject[0]["ID"]) }];
                }
                formParams.isFromMainMenu = false;
                if (pageParams.formOpenMode === FormOpenMode.insert) {
                    formParams.openMode = OpenModeType.insert;
                }
            }
        }

        return formParams;
    }

    private static _watchSessionTimeout(): void {
        OWExceptions.registerExceptionWatch(EOWSessionTimeOut.name, (exception) => {
            ErrorHandler.instance.handleError(exception);
        });
    }

    private static async _initActiveRoles(): Promise<void> {
        this._userSettingsManager = DIContainer.instance.create(IUserSettingsManager);
        AppContext.instance.setActiveUserRolesIds(await this._userSettingsManager.getActiveRolesIds());
        AppContext.instance.setActiveUserRolesRanges(this._userSettingsManager);
    }

    private static _registerActiveRolesSubscriber(): void {
        const dataEventsManager = DIContainer.instance.create(IDataEventsManager);
        dataEventsManager.subscribeActiveRolesChanged(async () => {
            AppContext.instance.setActiveUserRolesIds(await this._userSettingsManager.getActiveRolesIds());
            AppContext.instance.setActiveUserRolesRanges(this._userSettingsManager);
            this._reloadMenu();
            DIContainer.instance.create(ISystemOperationRights).clearCache();
            MainForm.activeRolesChanged.trigger(this);
        });
    }

    private static _reloadMenu(): void {
        dispatchAction(createReloadMenuAction());
    }

    /**
     * Inicializuje a vloží vybranou konfiguraci aplikace do redux-store.
     */
    private static _initReduxStore(): void {
        StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);
    }
}
